/**
 * Landing Page Builder V2 - Constants
 * Schedule/calendar display, live banner timing, and countdown ticks.
 */

import type { ScheduleDisplayStyle } from "../types/db-types";

/** Maximum number of days shown at once in the schedule Calendar view */
export const SCHEDULE_CALENDAR_MAX_DAYS_PER_PAGE = 7;

/** Pixel height of a single hour row on the MOBILE/base schedule Calendar time grid. Desktop uses the
 *  taller SCHEDULE_CALENDAR_DESKTOP_HOUR_HEIGHT_PX; this is also the floor the mobile fit-to-viewport
 *  math grows from. Sized so timed event blocks stay large and tappable on small screens. */
export const SCHEDULE_CALENDAR_HOUR_HEIGHT_PX = 128;

/** Pixel height of a single hour row on the desktop Calendar time grid (taller than mobile so events breathe) */
export const SCHEDULE_CALENDAR_DESKTOP_HOUR_HEIGHT_PX = 120;

/** Max height (px) of the desktop schedule time grid — content scrolls inside; shorter days shrink to fit */
export const SCHEDULE_CALENDAR_DESKTOP_MAX_VIEWPORT_PX = 720;

/** Fallback hour range when no timed items exist on the current calendar page */
export const SCHEDULE_CALENDAR_FALLBACK_START_HOUR = 8;
export const SCHEDULE_CALENDAR_FALLBACK_END_HOUR = 20;

/** Assumed duration (minutes) for items missing a start_time or end_time */
export const SCHEDULE_CALENDAR_DEFAULT_DURATION_MIN = 60;

/** Fixed viewport height (px) for the mobile schedule time grid — content scrolls inside */
export const SCHEDULE_CALENDAR_MOBILE_VIEWPORT_PX = 480;

/**
 * Vertical space (px) reserved for the focused schedule page's top bar + padding when sizing its
 * full-height calendar grid. The focused view subtracts this from the measured viewport height so
 * the grid fills the screen without pushing the back-bar off-screen. See FocusedScheduleView.
 */
export const SCHEDULE_FOCUSED_VIEW_CHROME_PX = 160;

/**
 * Fallback height (px) for the focused schedule calendar grid before the real viewport height is
 * measured (SSR / first client paint). Replaced once the focused view measures `window.innerHeight`.
 */
export const SCHEDULE_FOCUSED_FALLBACK_VIEWPORT_PX = 720;

/** Lower bound for the pinch-zoom hour-row scale on the mobile schedule time grid */
export const SCHEDULE_HOUR_SCALE_MIN = 0.5;
/** Upper bound for the pinch-zoom hour-row scale on the mobile schedule time grid */
export const SCHEDULE_HOUR_SCALE_MAX = 2.5;
/** Initial hour-row scale (1× = unscaled `SCHEDULE_CALENDAR_HOUR_HEIGHT_PX`) */
export const SCHEDULE_HOUR_SCALE_DEFAULT = 1;

/** Fallback color for a schedule label that has no `color` set (indigo-500). */
export const SCHEDULE_DEFAULT_LABEL_COLOR = "#6366f1";

/** Display-style options shown in the schedule editor + config modal. Single source of truth. */
export const SCHEDULE_DISPLAY_STYLE_OPTIONS: { value: ScheduleDisplayStyle; label: string }[] = [
  { value: "accordion", label: "Accordion" },
  { value: "cards", label: "Cards" },
  { value: "carousel", label: "Carousel" },
  { value: "calendar", label: "Calendar" },
  { value: "agenda", label: "Agenda" },
];

/** Auto-advance interval (ms) for the live banner carousel cycling currently-live items. */
export const CAROUSEL_ADVANCE_MS = 5000;
/**
 * Lead time (minutes) for the live banner's "NEXT" slides: while something is live, items
 * starting within this window are appended to the carousel after the "NOW" slides. Keeps the
 * banner showing what's about to start without crowding it with the whole rest of the day.
 */
export const SCHEDULE_UPCOMING_SOON_MINUTES = 15;
/**
 * Window (minutes) for the live banner's "next" state, when nothing is currently live: the banner
 * shows the next item plus any later items starting within this window of now (typically the next
 * hour). When the next item is further out than this, only that single item is shown.
 */
export const SCHEDULE_NEXT_WINDOW_MINUTES = 60;
/** Tick interval (ms) for the schedule clock (live banner + calendar now-line). Minute resolution. */
export const CLOCK_TICK_MS = 60000;
/** Tick interval (ms) for the pre-event banner countdown — second resolution for a live digital clock. */
export const COUNTDOWN_TICK_MS = 1000;

/**
 * Minimum WCAG contrast ratio for the countdown's accent-colored digits to be used over the band
 * background. Below this the renderer falls back to a guaranteed-readable color. 3:1 is the AA
 * threshold for large text, which the digits are (≥ 5xl bold).
 */
export const COUNTDOWN_ACCENT_MIN_CONTRAST = 3;
